/**
 * Mock data for Automate module — Workflow Orchestration Engine
 *
 * 8 automations (5 scheduled + 3 event-triggered)
 * 4 workflows with node/edge definitions
 * 7 playbook templates
 * 20 execution history entries
 */

// ── Types ──────────────────────────────────────────────────────────────────

export type TriggerType = 'scheduled' | 'event';
export type AutomationStatus = 'active' | 'paused' | 'error';
export type WorkflowStatus = 'active' | 'paused' | 'draft' | 'archived';
export type ExecutionStatus = 'completed' | 'failed' | 'running' | 'cancelled';
export type ExecutionType = 'scheduled' | 'event' | 'workflow' | 'manual' | 'playbook';
// NodeType — kept in lockstep with backend `WORKFLOW_NODE_KINDS` enum
// at packages/api/src/db/schema/workflow-definitions.ts. The 28 kinds
// here MUST match the backend enum exactly; the test suite enforces
// this invariant. Adding a kind here without adding it to the backend
// (or vice versa) was the root cause of round-7 NEW-MAJOR-3:
// users could drag a UI-only node onto the canvas, but saving the
// workflow would silently drop it (or fail honest validation).
//
// Five UI-only kinds were removed in fix/automate-carryover-majors:
//   http_response, date_time, template, parse_generate, workspace_handoff
// — these had no backend support and must be re-added on both sides
// together if/when they ship as real product features.
//
// AUTO-3 pod added two kinds (transform, wait_for_condition) and
// wired backend runtimes for eight previously enum-only kinds
// (filter, aggregate, split_out, sort, limit, remove_duplicates,
// switch, merge) — see automate-runner.service.ts.
export type NodeType =
  // Core (16)
  | 'trigger' | 'agent' | 'mcp_tool' | 'condition' | 'ai_router' | 'approval'
  | 'loop' | 'delay' | 'webhook' | 'notification'
  | 'llm_generate' | 'rag_search' | 'set_variable' | 'merge' | 'code' | 'sub_workflow'
  // Data manipulation (7) — backend runtimes wired by AUTO-3
  | 'switch' | 'filter' | 'aggregate' | 'split_out' | 'sort' | 'limit' | 'remove_duplicates'
  // Error handling (2)
  | 'error_handler' | 'stop'
  // Communication (1)
  | 'wait_for_event'
  // AUTO-3 net-new (2) — per-item template mapping + predicate polling
  | 'transform' | 'wait_for_condition'
  // Sprint 8 net-new (1) — transactional outbox emit (bSP8)
  | 'emit_event';
export type PlaybookCategory = 'maintenance' | 'safety' | 'compliance' | 'operations' | 'documentation';
// Event catalog (extended 2026-05-07 for the email-draft scenario + parity
// with general-purpose automation tools). Trigger.eventType drives both the
// matcher in the runtime *and* the per-type filter sub-form rendered by the
// property panel — see EVENT_TYPE_FILTER_FIELDS below.
export type EventType =
  // Operational
  | 'alarm_triggered' | 'alarm_cleared' | 'threshold_exceeded'
  | 'work_order_overdue' | 'work_order_state_changed'
  | 'certification_expiring' | 'safety_incident_logged'
  // Documents / Library
  | 'document_uploaded' | 'document_changed'
  // Communication
  | 'incoming_email' | 'webhook_inbound'
  // IoT / streaming
  | 'mqtt_message' | 'sensor_value_changed'
  // Approvals / governance
  | 'approval_pending' | 'approval_decision'
  // Identity / system
  | 'user_invited' | 'user_role_changed'
  // Custom (configured per-tenant)
  | 'custom';

export interface Automation {
  id: string;
  name: string;
  description: string;
  agentId: string;
  agentName: string;
  agentIcon: string;
  agentIconColor: string;
  triggerType: TriggerType;
  cronExpression?: string;
  eventType?: EventType;
  eventConditions?: Record<string, unknown>;
  isEnabled: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
  status: AutomationStatus;
  successRate: number;
  totalRuns: number;
  createdAt: string;
  createdBy: string;
}

export interface WorkflowNode {
  id: string;
  type: NodeType;
  position: { x: number; y: number };
  data: { label: string; config: Record<string, unknown> };
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  label?: string;
  animated?: boolean;
}

export type SupervisorMode = 'minimal' | 'standard' | 'thorough';
export type CriteriaType = 'completeness' | 'accuracy' | 'format' | 'safety' | 'compliance';
export type CriteriaEval = 'ai' | 'deterministic' | 'hybrid';
export type CriteriaWeight = 'critical' | 'important' | 'nice_to_have';
export type WorkflowTriggerType = 'schedule' | 'event' | 'webhook' | 'manual';
export type WorkflowComplexity = 'simple' | 'standard' | 'complex';

export interface SuccessCriterion {
  id: string;
  description: string;
  type: CriteriaType;
  evaluation: CriteriaEval;
  weight: CriteriaWeight;
}

export interface Workflow {
  id: string;
  name: string;
  description: string;
  status: WorkflowStatus;
  isEnabled?: boolean;
  // Visual graph
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  nodeCount: number;
  // Execution stats
  runCount: number;
  lastRunAt: string | null;
  avgDurationMs: number;
  successRate: number;
  // Purpose & Intelligence (AI Supervisor)
  purpose: string;
  successCriteria: SuccessCriterion[];
  constraints: string[];
  supervisorMode: SupervisorMode;
  // Trigger
  triggerType: WorkflowTriggerType;
  cronExpression?: string;
  eventType?: EventType;
  // Complexity indicator (simple = 2 nodes like old automations, standard = 3-8, complex = 9+)
  complexity: WorkflowComplexity;
  // Metadata
  agentId?: string;
  agentName?: string;
  agentIcon?: string;
  agentIconColor?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface PlaybookStep {
  step: number;
  name: string;
  description: string;
  toolUsed?: string;
  onFailure: 'stop' | 'continue' | 'branch';
  branchCondition?: string;
}

export interface Playbook {
  id: string;
  name: string;
  description: string;
  category: PlaybookCategory;
  agentId: string;
  agentName: string;
  agentIcon: string;
  agentIconColor: string;
  steps: PlaybookStep[];
  estimatedDuration: string;
  successRate: number;
  tags: string[];
}

export interface ExecutionStep {
  step: number;
  name: string;
  toolUsed?: string;
  status: ExecutionStatus;
  durationMs: number;
  input?: string;
  output?: string;
  error?: string;
}

export interface ExecutionEntry {
  id: string;
  type: ExecutionType;
  sourceId: string;
  sourceName: string;
  agentId?: string;
  agentName?: string;
  agentIcon?: string;
  agentIconColor?: string;
  status: ExecutionStatus;
  trigger: string;
  startedAt: string;
  completedAt: string | null;
  durationMs: number;
  stepsCompleted: number;
  totalSteps?: number;
  stepsFailed: number;
  steps?: ExecutionStep[];
  error?: string;
}

// ── Label & Style Maps ─────────────────────────────────────────────────────

export const TRIGGER_TYPE_LABELS: Record<TriggerType, { label: string; color: string; icon: string }> = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30', icon: 'clock' },
  event: { label: 'Event', color: 'bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30', icon: 'zap' },
};

export const AUTOMATION_STATUS_STYLES: Record<AutomationStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20' },
  paused: { label: 'Paused', color: 'bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30' },
  error: { label: 'Error', color: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30' },
};

export const WORKFLOW_STATUS_STYLES: Record<WorkflowStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20' },
  paused: { label: 'Paused', color: 'bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30' },
  draft: { label: 'Draft', color: 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/30' },
  archived: { label: 'Archived', color: 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/30' },
};

export const EXECUTION_STATUS_STYLES: Record<ExecutionStatus, { label: string; color: string; icon: string }> = {
  completed: { label: 'Completed', color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20', icon: 'check-circle' },
  failed: { label: 'Failed', color: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30', icon: 'x-circle' },
  running: { label: 'Running', color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30', icon: 'loader-2' },
  cancelled: { label: 'Cancelled', color: 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/30', icon: 'ban' },
};

export const EXECUTION_TYPE_STYLES: Record<ExecutionType, { label: string; color: string }> = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  event: { label: 'Event', color: 'bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30' },
  workflow: { label: 'Workflow', color: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30' },
  manual: { label: 'Manual', color: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/20' },
  playbook: { label: 'Playbook', color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20' },
};

export const PLAYBOOK_CATEGORY_STYLES: Record<PlaybookCategory, { label: string; color: string }> = {
  maintenance: { label: 'Maintenance', color: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/20' },
  safety: { label: 'Safety', color: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30' },
  compliance: { label: 'Compliance', color: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30' },
  operations: { label: 'Operations', color: 'bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30' },
  documentation: { label: 'Documentation', color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30' },
};

export const NODE_TYPE_CONFIG: Record<NodeType, { label: string; icon: string; color: string; borderColor: string; category: string }> = {
  trigger: { label: 'Trigger', icon: 'zap', color: 'text-amber-800 dark:text-amber-300', borderColor: 'border-l-amber-500', category: 'Triggers' },
  agent: { label: 'Agent', icon: 'bot', color: 'text-purple-600 dark:text-purple-400', borderColor: 'border-l-purple-500', category: 'AI & Agents' },
  mcp_tool: { label: 'Tool', icon: 'plug', color: 'text-blue-700 dark:text-blue-300', borderColor: 'border-l-blue-500', category: 'Actions' },
  condition: { label: 'Condition', icon: 'git-branch', color: 'text-emerald-600 dark:text-emerald-400', borderColor: 'border-l-emerald-500', category: 'Logic' },
  ai_router: { label: 'AI Router', icon: 'sparkles', color: 'text-violet-700 dark:text-violet-300', borderColor: 'border-l-violet-500', category: 'AI & Agents' },
  approval: { label: 'Approval', icon: 'shield-check', color: 'text-red-700 dark:text-red-300', borderColor: 'border-l-red-500', category: 'Governance' },
  loop: { label: 'Loop', icon: 'repeat', color: 'text-indigo-600 dark:text-indigo-400', borderColor: 'border-l-indigo-500', category: 'Logic' },
  delay: { label: 'Delay', icon: 'clock', color: 'text-slate-600 dark:text-slate-400', borderColor: 'border-l-slate-500', category: 'Logic' },
  webhook: { label: 'HTTP Request', icon: 'globe', color: 'text-sky-600 dark:text-sky-400', borderColor: 'border-l-sky-500', category: 'Actions' },
  notification: { label: 'Notification', icon: 'bell', color: 'text-orange-600 dark:text-orange-400', borderColor: 'border-l-orange-500', category: 'Actions' },
  llm_generate: { label: 'LLM Generate', icon: 'sparkles', color: 'text-pink-600 dark:text-pink-400', borderColor: 'border-l-pink-500', category: 'AI & Agents' },
  rag_search: { label: 'RAG Search', icon: 'search', color: 'text-cyan-600 dark:text-cyan-400', borderColor: 'border-l-cyan-500', category: 'AI & Agents' },
  set_variable: { label: 'Set Variable', icon: 'variable', color: 'text-teal-600 dark:text-teal-400', borderColor: 'border-l-teal-500', category: 'Data' },
  merge: { label: 'Merge', icon: 'git-merge', color: 'text-fuchsia-600 dark:text-fuchsia-400', borderColor: 'border-l-fuchsia-500', category: 'Data' },
  code: { label: 'Code', icon: 'code', color: 'text-lime-600 dark:text-lime-400', borderColor: 'border-l-lime-500', category: 'Transform' },
  sub_workflow: { label: 'Sub-Workflow', icon: 'workflow', color: 'text-rose-600 dark:text-rose-400', borderColor: 'border-l-rose-500', category: 'Logic' },
  // Data Manipulation
  switch: { label: 'Switch', icon: 'route', color: 'text-green-600 dark:text-green-400', borderColor: 'border-l-green-500', category: 'Logic' },
  filter: { label: 'Filter', icon: 'filter', color: 'text-blue-700 dark:text-blue-300', borderColor: 'border-l-blue-500', category: 'Data' },
  aggregate: { label: 'Aggregate', icon: 'sigma', color: 'text-amber-800 dark:text-amber-300', borderColor: 'border-l-amber-500', category: 'Data' },
  split_out: { label: 'Split Out', icon: 'unfold-vertical', color: 'text-purple-600 dark:text-purple-400', borderColor: 'border-l-purple-500', category: 'Data' },
  sort: { label: 'Sort', icon: 'arrow-up-down', color: 'text-cyan-600 dark:text-cyan-400', borderColor: 'border-l-cyan-500', category: 'Data' },
  limit: { label: 'Limit', icon: 'list-end', color: 'text-zinc-700 dark:text-zinc-300', borderColor: 'border-l-zinc-500', category: 'Data' },
  remove_duplicates: { label: 'Remove Duplicates', icon: 'copy-minus', color: 'text-orange-600 dark:text-orange-400', borderColor: 'border-l-orange-500', category: 'Data' },
  // Error Handling
  error_handler: { label: 'Error Handler', icon: 'shield-alert', color: 'text-red-700 dark:text-red-300', borderColor: 'border-l-red-500', category: 'Governance' },
  stop: { label: 'Stop', icon: 'octagon', color: 'text-red-700 dark:text-red-300', borderColor: 'border-l-red-500', category: 'Governance' },
  // Communication
  wait_for_event: { label: 'Wait for Event', icon: 'hourglass', color: 'text-violet-700 dark:text-violet-300', borderColor: 'border-l-violet-500', category: 'Logic' },
  // AUTO-3 net-new node kinds
  transform: { label: 'Transform', icon: 'wand-2', color: 'text-teal-600 dark:text-teal-400', borderColor: 'border-l-teal-500', category: 'Transform' },
  wait_for_condition: { label: 'Wait for Condition', icon: 'timer', color: 'text-violet-700 dark:text-violet-300', borderColor: 'border-l-violet-500', category: 'Logic' },
  // Sprint 8 — transactional outbox event emitter
  emit_event: { label: 'Emit Event', icon: 'radio', color: 'text-rose-600 dark:text-rose-400', borderColor: 'border-l-rose-500', category: 'Automation' },
};

export type NodeRuntimeStatus = 'ready' | 'partial' | 'unsupported';

export interface NodeRuntimeSupport {
  status: NodeRuntimeStatus;
  label: string;
  note: string;
}

// All node kinds now have a wired production runtime in
// automate-runner.service.ts (verified by the node-kind parity test). The
// 2026-06 hardening campaign wired code (#1449), ai_router (#1451),
// error_handler (#1452), loop (#1455), sub_workflow (#1456), wait_for_event
// (#1460), and llm_generate — so the palette no longer disables them.
export const UNSUPPORTED_RUNTIME_NODE_TYPES: readonly NodeType[] = [];

export const PARTIAL_RUNTIME_NODE_TYPES: readonly NodeType[] = [
  'notification',
];

export function getNodeRuntimeSupport(nodeType: NodeType): NodeRuntimeSupport {
  if (UNSUPPORTED_RUNTIME_NODE_TYPES.includes(nodeType)) {
    return {
      status: 'unsupported',
      label: 'Not wired',
      note: 'This node is in the schema but does not have a production runtime yet.',
    };
  }
  if (PARTIAL_RUNTIME_NODE_TYPES.includes(nodeType)) {
    return {
      status: 'partial',
      label: 'Partial',
      note: 'This node records intent, but external delivery is deferred.',
    };
  }
  return {
    status: 'ready',
    label: 'Ready',
    note: 'This node has a wired production runtime.',
  };
}

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  // Operational
  alarm_triggered: 'Alarm Triggered',
  alarm_cleared: 'Alarm Cleared',
  threshold_exceeded: 'Threshold Exceeded',
  work_order_overdue: 'Work Order Overdue',
  work_order_state_changed: 'Work Order State Changed',
  certification_expiring: 'Certification Expiring',
  safety_incident_logged: 'Safety Incident Logged',
  // Documents / Library
  document_uploaded: 'Document Uploaded',
  document_changed: 'Document Updated',
  // Communication
  incoming_email: 'Incoming Email',
  webhook_inbound: 'Inbound Webhook',
  // IoT / streaming
  mqtt_message: 'MQTT Message',
  sensor_value_changed: 'Sensor Value Changed',
  // Approvals / governance
  approval_pending: 'Approval Requested',
  approval_decision: 'Approval Decided',
  // Identity / system
  user_invited: 'User Invited',
  user_role_changed: 'User Role Changed',
  // Custom
  custom: 'Custom Event',
};

// Group event types so the property panel can render an organised
// dropdown instead of a flat list of 18 items. Order here is render
// order in the dropdown.
export const EVENT_TYPE_GROUPS: Array<{ label: string; events: EventType[] }> = [
  {
    label: 'Operational',
    events: [
      'alarm_triggered', 'alarm_cleared', 'threshold_exceeded',
      'work_order_overdue', 'work_order_state_changed',
      'certification_expiring', 'safety_incident_logged',
    ],
  },
  { label: 'Documents', events: ['document_uploaded', 'document_changed'] },
  { label: 'Communication', events: ['incoming_email', 'webhook_inbound'] },
  { label: 'IoT / Streaming', events: ['mqtt_message', 'sensor_value_changed'] },
  { label: 'Approvals', events: ['approval_pending', 'approval_decision'] },
  { label: 'Identity', events: ['user_invited', 'user_role_changed'] },
  { label: 'Custom', events: ['custom'] },
];

// Per-event-type filter field schema. Drives the sub-form the trigger
// node property panel renders once the user picks an event type. Each
// field becomes a key under config.eventFilter.{key}.
export type EventFilterFieldKind =
  | 'text' | 'select' | 'number' | 'boolean' | 'multiselect';

export interface EventFilterField {
  key: string;
  label: string;
  kind: EventFilterFieldKind;
  placeholder?: string;
  helper?: string;
  options?: Array<{ value: string; label: string }>;
}

export const EVENT_TYPE_FILTER_FIELDS: Partial<Record<EventType, EventFilterField[]>> = {
  alarm_triggered: [
    { key: 'severity', label: 'Severity (any of)', kind: 'multiselect',
      options: [
        { value: 'critical', label: 'Critical' },
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' },
        { value: 'info', label: 'Info' },
      ],
      helper: 'Empty matches all severities' },
    { key: 'assetTagPattern', label: 'Asset tag matches', kind: 'text', placeholder: 'P-* or HX-301' },
    { key: 'sourceSystem', label: 'Source system', kind: 'text', placeholder: 'iq-telemetry' },
  ],
  alarm_cleared: [
    { key: 'assetTagPattern', label: 'Asset tag matches', kind: 'text', placeholder: 'P-* or HX-301' },
  ],
  threshold_exceeded: [
    { key: 'metricKey', label: 'Metric', kind: 'text', placeholder: 'vibration_rms' },
    { key: 'comparator', label: 'Comparator', kind: 'select',
      options: [
        { value: 'gt', label: 'Greater than' },
        { value: 'gte', label: 'Greater or equal' },
        { value: 'lt', label: 'Less than' },
        { value: 'lte', label: 'Less or equal' },
      ] },
    { key: 'thresholdValue', label: 'Threshold value', kind: 'number', placeholder: '4.5' },
  ],
  work_order_overdue: [
    { key: 'priority', label: 'Priority (any of)', kind: 'multiselect',
      options: [
        { value: 'critical', label: 'Critical' },
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' },
      ] },
    { key: 'assignedTeam', label: 'Assigned team', kind: 'text', placeholder: 'Mech-A' },
  ],
  work_order_state_changed: [
    { key: 'fromState', label: 'From state', kind: 'text', placeholder: 'in_progress' },
    { key: 'toState', label: 'To state', kind: 'text', placeholder: 'completed' },
  ],
  certification_expiring: [
    { key: 'daysAhead', label: 'Days before expiry', kind: 'number', placeholder: '30' },
    { key: 'certType', label: 'Certification type', kind: 'text', placeholder: 'OSHA 510' },
  ],
  safety_incident_logged: [
    { key: 'severity', label: 'Severity (any of)', kind: 'multiselect',
      options: [
        { value: 'near_miss', label: 'Near miss' },
        { value: 'first_aid', label: 'First aid' },
        { value: 'recordable', label: 'Recordable' },
        { value: 'lost_time', label: 'Lost-time' },
      ] },
  ],
  document_uploaded: [
    { key: 'folderId', label: 'Folder ID', kind: 'text', placeholder: 'folder-eng-sops' },
    { key: 'classification', label: 'Classification (any of)', kind: 'multiselect',
      options: [
        { value: 'unclassified', label: 'Unclassified' },
        { value: 'cui', label: 'CUI' },
        { value: 'restricted', label: 'Restricted' },
      ] },
  ],
  document_changed: [
    { key: 'folderId', label: 'Folder ID', kind: 'text', placeholder: 'folder-eng-sops' },
  ],
  incoming_email: [
    { key: 'mailbox', label: 'Mailbox', kind: 'text', placeholder: 'support@argus.local',
      helper: 'Connected via Connections (Outlook / Gmail / IMAP). Leave blank for any.' },
    { key: 'fromPattern', label: 'From matches', kind: 'text', placeholder: '*@customer.com' },
    { key: 'subjectPattern', label: 'Subject matches', kind: 'text', placeholder: 'Issue #*' },
    { key: 'hasAttachment', label: 'Has attachment', kind: 'boolean' },
    { key: 'label', label: 'Mailbox label/folder', kind: 'text', placeholder: 'Inbox/Support' },
  ],
  webhook_inbound: [
    { key: 'method', label: 'Method', kind: 'select',
      options: [
        { value: 'POST', label: 'POST' },
        { value: 'PUT', label: 'PUT' },
        { value: 'PATCH', label: 'PATCH' },
        { value: 'GET', label: 'GET' },
      ] },
    { key: 'authMode', label: 'Auth', kind: 'select',
      options: [
        { value: 'none', label: 'None (open URL)' },
        { value: 'shared_secret', label: 'Shared secret header' },
        { value: 'hmac', label: 'HMAC signature' },
        { value: 'bearer', label: 'Bearer token' },
      ] },
    { key: 'pathSuffix', label: 'Path suffix', kind: 'text',
      placeholder: '/orders', helper: 'Optional — appended to the workflow base webhook URL.' },
  ],
  mqtt_message: [
    { key: 'topic', label: 'Topic', kind: 'text', placeholder: 'plant-a/+/temperature' },
    { key: 'qos', label: 'QoS', kind: 'select',
      options: [
        { value: '0', label: '0 — at most once' },
        { value: '1', label: '1 — at least once' },
        { value: '2', label: '2 — exactly once' },
      ] },
  ],
  sensor_value_changed: [
    { key: 'assetTagPattern', label: 'Asset tag matches', kind: 'text', placeholder: 'BR1-PUMP-*' },
    { key: 'metricKey', label: 'Metric', kind: 'text', placeholder: 'temperature_c' },
    { key: 'minDelta', label: 'Min change', kind: 'number',
      placeholder: '0.5', helper: 'Suppress noise: only fire when value moves by this much.' },
  ],
  approval_pending: [
    { key: 'approvalKind', label: 'Approval kind', kind: 'text', placeholder: 'workflow-write' },
  ],
  approval_decision: [
    { key: 'decision', label: 'Decision', kind: 'select',
      options: [
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'expired', label: 'Expired' },
      ] },
  ],
  user_invited: [],
  user_role_changed: [
    { key: 'fromRole', label: 'From role', kind: 'text', placeholder: 'member' },
    { key: 'toRole', label: 'To role', kind: 'text', placeholder: 'admin' },
  ],
  custom: [
    { key: 'eventName', label: 'Custom event name', kind: 'text',
      placeholder: 'plant.shift.handover',
      helper: 'Match an event posted to the workflow event bus by an upstream system.' },
  ],
};

export const CRON_PRESETS = [
  { label: 'Every 15 minutes', value: '*/15 * * * *' },
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Every 6 hours', value: '0 */6 * * *' },
  { label: 'Daily at 6 AM', value: '0 6 * * *' },
  { label: 'Daily at 6 PM', value: '0 18 * * *' },
  { label: 'Daily at 8 AM', value: '0 8 * * *' },
  { label: 'Weekly Monday 9 AM', value: '0 9 * * 1' },
  { label: 'Monthly 1st at 9 AM', value: '0 9 1 * *' },
  { label: 'Quarterly (Jan, Apr, Jul, Oct)', value: '0 9 1 1,4,7,10 *' },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

function hoursAgo(h: number): string { return new Date(Date.now() - h * 3600000).toISOString(); }
function minsAgo(m: number): string { return new Date(Date.now() - m * 60000).toISOString(); }
function daysAgo(d: number): string { return new Date(Date.now() - d * 86400000).toISOString(); }
function hoursFromNow(h: number): string { return new Date(Date.now() + h * 3600000).toISOString(); }

// ── Mock Automations ────────────────────────────────────────────────────────

export const MOCK_AUTOMATIONS: Automation[] = [
  {
    id: 'auto-1', name: 'Morning Shift Report', description: 'Generate and post shift handover report for day shift every morning at 6 AM',
    agentId: 'agent-shift', agentName: 'Shift Handover', agentIcon: 'notebook', agentIconColor: 'amber',
    triggerType: 'scheduled', cronExpression: '0 6 * * *',
    isEnabled: true, lastRunAt: hoursAgo(6), nextRunAt: hoursFromNow(18),
    status: 'active', successRate: 0.98, totalRuns: 240,
    createdAt: daysAgo(120), createdBy: 'Jay Talreja',
  },
  {
    id: 'auto-2', name: 'Evening Shift Report', description: 'Generate and post shift handover report for night shift every evening at 6 PM',
    agentId: 'agent-shift', agentName: 'Shift Handover', agentIcon: 'notebook', agentIconColor: 'amber',
    triggerType: 'scheduled', cronExpression: '0 18 * * *',
    isEnabled: true, lastRunAt: hoursAgo(18), nextRunAt: hoursFromNow(6),
    status: 'active', successRate: 0.97, totalRuns: 238,
    createdAt: daysAgo(120), createdBy: 'Jay Talreja',
  },
  {
    id: 'auto-3', name: 'Weekly Reliability Report', description: 'Generate reliability analysis report with MTBF, MTTR, and failure trends every Monday morning',
    agentId: 'agent-reliability', agentName: 'Reliability', agentIcon: 'bar-chart', agentIconColor: 'emerald',
    triggerType: 'scheduled', cronExpression: '0 9 * * 1',
    isEnabled: true, lastRunAt: daysAgo(3), nextRunAt: hoursFromNow(96),
    status: 'active', successRate: 0.95, totalRuns: 52,
    createdAt: daysAgo(365), createdBy: 'Sarah Johnson',
  },
  {
    id: 'auto-4', name: 'Daily Compliance Check', description: 'Run compliance monitoring checks against all active frameworks (ITAR, CMMC) every morning',
    agentId: 'agent-compliance', agentName: 'Compliance Monitor', agentIcon: 'shield-check', agentIconColor: 'violet',
    triggerType: 'scheduled', cronExpression: '0 8 * * *',
    isEnabled: true, lastRunAt: hoursAgo(12), nextRunAt: hoursFromNow(12),
    status: 'error', successRate: 0.85, totalRuns: 180,
    createdAt: daysAgo(200), createdBy: 'Admin',
  },
  {
    id: 'auto-5', name: 'Quarterly ITAR Audit', description: 'Full ITAR compliance audit with document classification verification and access control review',
    agentId: 'agent-compliance', agentName: 'Compliance Monitor', agentIcon: 'shield-check', agentIconColor: 'violet',
    triggerType: 'scheduled', cronExpression: '0 9 1 1,4,7,10 *',
    isEnabled: false, lastRunAt: daysAgo(45), nextRunAt: null,
    status: 'paused', successRate: 1.0, totalRuns: 4,
    createdAt: daysAgo(400), createdBy: 'Admin',
  },
  {
    id: 'auto-6', name: 'High Vibration Alert', description: 'Trigger maintenance analysis when any equipment vibration reading exceeds alert threshold',
    agentId: 'agent-maintenance', agentName: 'Maintenance Intelligence', agentIcon: 'wrench', agentIconColor: 'cyan',
    triggerType: 'event', eventType: 'threshold_exceeded',
    eventConditions: { parameter: 'vibration_rms', operator: '>', threshold: 4.5 },
    isEnabled: true, lastRunAt: hoursAgo(2), nextRunAt: null,
    status: 'active', successRate: 0.92, totalRuns: 47,
    createdAt: daysAgo(90), createdBy: 'Mike Chen',
  },
  {
    id: 'auto-7', name: 'Work Order Overdue', description: 'Alert work order planner when any work order exceeds its due date by more than 24 hours',
    agentId: 'agent-work-order', agentName: 'Work Order Planner', agentIcon: 'clipboard-check', agentIconColor: 'amber',
    triggerType: 'event', eventType: 'work_order_overdue',
    isEnabled: true, lastRunAt: daysAgo(1), nextRunAt: null,
    status: 'active', successRate: 0.88, totalRuns: 23,
    createdAt: daysAgo(60), createdBy: 'Sarah Johnson',
  },
  {
    id: 'auto-8', name: 'Certification Expiring', description: 'Notify compliance team 90 days before any certification expires',
    agentId: 'agent-compliance', agentName: 'Compliance Monitor', agentIcon: 'shield-check', agentIconColor: 'violet',
    triggerType: 'event', eventType: 'certification_expiring',
    isEnabled: true, lastRunAt: daysAgo(5), nextRunAt: null,
    status: 'active', successRate: 1.0, totalRuns: 12,
    createdAt: daysAgo(180), createdBy: 'Admin',
  },
];

// ── Mock Workflows ──────────────────────────────────────────────────────────

export const MOCK_WORKFLOWS: Workflow[] = [
  {
    id: 'wf-1', name: 'Equipment Troubleshooting', description: 'Multi-step diagnosis workflow: lookup asset, check telemetry, search knowledge base, generate recommendations',
    status: 'active',
    nodes: [
      { id: 'n1', type: 'trigger', position: { x: 50, y: 200 }, data: { label: 'Event Trigger', config: { triggerType: 'event', eventType: 'threshold_exceeded' } } },
      { id: 'n2', type: 'agent', position: { x: 300, y: 200 }, data: { label: 'Asset Lookup', config: { agentId: 'agent-maintenance', agentName: 'Maintenance Intelligence', prompt: 'Look up the affected asset and its recent maintenance history', model: 'default', tools: ['iq.asset.search', 'iq.asset.get', 'iq.asset.getHistory'], skills: ['maintenance_analysis'], knowledgeFolders: ['folder-eng-manuals'], maxSteps: 10, temperature: 0.7 } } },
      { id: 'n3', type: 'mcp_tool', position: { x: 550, y: 100 }, data: { label: 'Get Telemetry', config: { tool: 'iq.telemetry.getHistory', params: { days: 30 } } } },
      { id: 'n4', type: 'mcp_tool', position: { x: 550, y: 300 }, data: { label: 'Search Knowledge', config: { tool: 'builtin.search_knowledge', params: { query: 'troubleshooting' } } } },
      { id: 'n5', type: 'agent', position: { x: 800, y: 200 }, data: { label: 'Analyze & Diagnose', config: { agentId: 'agent-troubleshooting', agentName: 'Troubleshooting', prompt: 'Analyze telemetry and documents to diagnose the issue', model: 'default', tools: ['iq.asset.search', 'iq.telemetry.getHistory', 'builtin.search_knowledge'], skills: ['troubleshooting', 'maintenance_analysis'], knowledgeFolders: ['folder-all'], maxSteps: 15, temperature: 0.5 } } },
      { id: 'n6', type: 'condition', position: { x: 1050, y: 200 }, data: { label: 'Severity Check', config: { field: 'severity', operator: 'gte', value: 'high' } } },
      { id: 'n7', type: 'approval', position: { x: 1300, y: 100 }, data: { label: 'Supervisor Approval', config: { level: 'supervisor', timeout: 240 } } },
      { id: 'n8', type: 'notification', position: { x: 1300, y: 300 }, data: { label: 'Send Report', config: { channel: 'email', recipients: ['maintenance-team'] } } },
    ],
    edges: [
      { id: 'e1', source: 'n1', target: 'n2' },
      { id: 'e2', source: 'n2', target: 'n3' },
      { id: 'e3', source: 'n2', target: 'n4' },
      { id: 'e4', source: 'n3', target: 'n5' },
      { id: 'e5', source: 'n4', target: 'n5' },
      { id: 'e6', source: 'n5', target: 'n6' },
      { id: 'e7', source: 'n6', target: 'n7', sourceHandle: 'true', label: 'High severity' },
      { id: 'e8', source: 'n6', target: 'n8', sourceHandle: 'false', label: 'Low severity' },
    ],
    nodeCount: 8, runCount: 89, lastRunAt: hoursAgo(2), avgDurationMs: 45000, successRate: 0.92,
    purpose: 'Diagnose equipment issues by analyzing telemetry data, maintenance history, and knowledge base documents. Produce a diagnosis with severity assessment and recommended corrective actions.',
    successCriteria: [
      { id: 'sc-1', description: 'Diagnosis must cite at least 2 data sources (telemetry + docs)', type: 'completeness', evaluation: 'ai', weight: 'critical' },
      { id: 'sc-2', description: 'Severity classification must match ISO 10816 thresholds', type: 'accuracy', evaluation: 'deterministic', weight: 'critical' },
      { id: 'sc-3', description: 'Recommendations must include estimated timeline and parts needed', type: 'completeness', evaluation: 'ai', weight: 'important' },
    ],
    constraints: ['Never recommend equipment shutdown without supervisor approval', 'All numerical values must cite their measurement source'],
    supervisorMode: 'standard',
    triggerType: 'event', eventType: 'threshold_exceeded',
    complexity: 'complex',
    agentId: 'agent-maintenance', agentName: 'Maintenance Intelligence', agentIcon: 'wrench', agentIconColor: 'cyan',
    createdAt: daysAgo(60), updatedAt: daysAgo(5), createdBy: 'Jay Talreja',
  },
  {
    id: 'wf-2', name: 'Shift Handover Pipeline', description: 'Automated shift handover: collect alarms, work orders, safety incidents, compile report, send to Slack and email',
    status: 'active',
    nodes: [
      { id: 'n1', type: 'trigger', position: { x: 50, y: 250 }, data: { label: 'Schedule', config: { triggerType: 'scheduled', cron: '0 6,18 * * *' } } },
      { id: 'n2', type: 'mcp_tool', position: { x: 300, y: 100 }, data: { label: 'Get Alarms', config: { tool: 'iq.telemetry.getAlarms' } } },
      { id: 'n3', type: 'mcp_tool', position: { x: 300, y: 250 }, data: { label: 'Get Work Orders', config: { tool: 'iq.cmms.getWorkOrders' } } },
      { id: 'n4', type: 'mcp_tool', position: { x: 300, y: 400 }, data: { label: 'Get Safety Log', config: { tool: 'iq.safety.getIncidents' } } },
      { id: 'n5', type: 'agent', position: { x: 600, y: 250 }, data: { label: 'Compile Report', config: { agentId: 'agent-shift', agentName: 'Shift Handover', prompt: 'Compile a comprehensive shift handover report from the collected data', model: 'default', tools: ['iq.telemetry.getAlarms', 'iq.cmms.getWorkOrders', 'iq.safety.getIncidents'], skills: ['shift_reporting'], knowledgeFolders: ['folder-all'], maxSteps: 8, temperature: 0.3 } } },
      { id: 'n6', type: 'notification', position: { x: 900, y: 150 }, data: { label: 'Post to Slack', config: { channel: 'slack', recipients: ['#operations'] } } },
      { id: 'n7', type: 'notification', position: { x: 900, y: 350 }, data: { label: 'Email Report', config: { channel: 'email', recipients: ['shift-managers'] } } },
    ],
    edges: [
      { id: 'e1', source: 'n1', target: 'n2' },
      { id: 'e2', source: 'n1', target: 'n3' },
      { id: 'e3', source: 'n1', target: 'n4' },
      { id: 'e4', source: 'n2', target: 'n5' },
      { id: 'e5', source: 'n3', target: 'n5' },
      { id: 'e6', source: 'n4', target: 'n5' },
      { id: 'e7', source: 'n5', target: 'n6' },
      { id: 'e8', source: 'n5', target: 'n7' },
    ],
    nodeCount: 7, runCount: 240, lastRunAt: hoursAgo(6), avgDurationMs: 32000, successRate: 0.98,
    purpose: 'Generate a comprehensive shift handover report covering all alarms, completed work orders, open issues, and safety status for the ending shift.',
    successCriteria: [
      { id: 'sc-4', description: 'Report must include ALL alarms from the shift period', type: 'completeness', evaluation: 'ai', weight: 'critical' },
      { id: 'sc-5', description: 'Each work order must show current status', type: 'accuracy', evaluation: 'deterministic', weight: 'critical' },
      { id: 'sc-6', description: 'Safety section must be present even if no incidents', type: 'format', evaluation: 'deterministic', weight: 'critical' },
    ],
    constraints: ['Report must be generated within 2 minutes', 'Never omit safety section even if empty'],
    supervisorMode: 'standard',
    triggerType: 'schedule', cronExpression: '0 6,18 * * *',
    complexity: 'standard',
    agentId: 'agent-shift', agentName: 'Shift Handover', agentIcon: 'notebook', agentIconColor: 'amber',
    createdAt: daysAgo(120), updatedAt: daysAgo(10), createdBy: 'Jay Talreja',
  },
  {
    id: 'wf-3', name: 'Document Onboarding', description: 'Ingest new document: classify, extract entities, link to assets, index for RAG, notify team',
    status: 'active',
    nodes: [
      { id: 'n1', type: 'trigger', position: { x: 50, y: 200 }, data: { label: 'Document Upload', config: { triggerType: 'event', eventType: 'document_uploaded' } } },
      { id: 'n2', type: 'agent', position: { x: 300, y: 200 }, data: { label: 'Classify Document', config: { agentId: 'agent-docs', agentName: 'Documentation & SOP', prompt: 'Classify this document type and sensitivity', model: 'fast', tools: ['iq.document.search', 'builtin.search_knowledge'], skills: ['document_qa'], knowledgeFolders: ['folder-all'], maxSteps: 5, temperature: 0.3 } } },
      { id: 'n3', type: 'agent', position: { x: 550, y: 200 }, data: { label: 'Extract Entities', config: { agentId: 'agent-docs', agentName: 'Documentation & SOP', prompt: 'Extract equipment tags, part numbers, chemical names', model: 'default', tools: ['iq.asset.search', 'iq.document.search'], skills: ['document_qa'], knowledgeFolders: ['folder-eng'], maxSteps: 8, temperature: 0.5 } } },
      { id: 'n4', type: 'mcp_tool', position: { x: 800, y: 100 }, data: { label: 'Link Assets', config: { tool: 'iq.asset.link' } } },
      { id: 'n5', type: 'mcp_tool', position: { x: 800, y: 300 }, data: { label: 'Index for RAG', config: { tool: 'builtin.index_document' } } },
      { id: 'n6', type: 'notification', position: { x: 1050, y: 200 }, data: { label: 'Notify Team', config: { channel: 'slack', recipients: ['#library-updates'] } } },
    ],
    edges: [
      { id: 'e1', source: 'n1', target: 'n2' },
      { id: 'e2', source: 'n2', target: 'n3' },
      { id: 'e3', source: 'n3', target: 'n4' },
      { id: 'e4', source: 'n3', target: 'n5' },
      { id: 'e5', source: 'n4', target: 'n6' },
      { id: 'e6', source: 'n5', target: 'n6' },
    ],
    nodeCount: 6, runCount: 62, lastRunAt: daysAgo(1), avgDurationMs: 68000, successRate: 0.90,
    purpose: 'Process newly uploaded documents through classification, entity extraction, asset linking, and RAG indexing to make them searchable.',
    successCriteria: [
      { id: 'sc-7', description: 'Document must be classified with correct type and sensitivity', type: 'accuracy', evaluation: 'ai', weight: 'critical' },
      { id: 'sc-8', description: 'All equipment tags must be extracted and linked to assets', type: 'completeness', evaluation: 'deterministic', weight: 'important' },
    ],
    constraints: ['ITAR documents must not be indexed without clearance verification'],
    supervisorMode: 'thorough',
    triggerType: 'event', eventType: 'document_uploaded',
    complexity: 'standard',
    agentId: 'agent-docs', agentName: 'Documentation & SOP', agentIcon: 'file-text', agentIconColor: 'blue',
    createdAt: daysAgo(45), updatedAt: daysAgo(3), createdBy: 'Admin',
  },
  {
    id: 'wf-4', name: 'Compliance Audit Pipeline', description: 'Full compliance audit: check frameworks, verify documents, score compliance, generate report, request sign-off',
    status: 'draft',
    nodes: [
      { id: 'n1', type: 'trigger', position: { x: 50, y: 200 }, data: { label: 'Manual Trigger', config: { triggerType: 'manual' } } },
      { id: 'n2', type: 'agent', position: { x: 300, y: 200 }, data: { label: 'Check Frameworks', config: { agentId: 'agent-compliance', agentName: 'Compliance Monitor', model: 'default', tools: ['iq.compliance.getFrameworks', 'iq.document.search'], skills: ['compliance_check'], knowledgeFolders: ['folder-all'], maxSteps: 10 } } },
      { id: 'n3', type: 'loop', position: { x: 550, y: 200 }, data: { label: 'For Each Framework', config: { type: 'iterate', collection: 'frameworks' } } },
      { id: 'n4', type: 'agent', position: { x: 800, y: 200 }, data: { label: 'Verify Docs', config: { agentId: 'agent-compliance', agentName: 'Compliance Monitor', model: 'default', tools: ['iq.document.search', 'builtin.search_knowledge'], skills: ['compliance_check', 'document_qa'], knowledgeFolders: ['folder-compliance'], maxSteps: 12 } } },
      { id: 'n5', type: 'agent', position: { x: 1050, y: 200 }, data: { label: 'Score Compliance', config: { agentId: 'agent-compliance', agentName: 'Compliance Monitor', model: 'precise', tools: ['iq.compliance.getFrameworks'], skills: ['compliance_check'], knowledgeFolders: ['folder-all'], maxSteps: 8 } } },
      { id: 'n6', type: 'agent', position: { x: 1300, y: 200 }, data: { label: 'Generate Report', config: { agentId: 'agent-compliance', agentName: 'Compliance Monitor', model: 'default', tools: ['iq.compliance.getFrameworks', 'iq.document.search'], skills: ['compliance_check'], knowledgeFolders: ['folder-all'], maxSteps: 10, temperature: 0.3 } } },
      { id: 'n7', type: 'approval', position: { x: 1550, y: 200 }, data: { label: 'Sign-off', config: { level: 'two_person' } } },
      { id: 'n8', type: 'notification', position: { x: 1800, y: 100 }, data: { label: 'Publish Report', config: { channel: 'email' } } },
      { id: 'n9', type: 'webhook', position: { x: 1800, y: 300 }, data: { label: 'Archive to S3', config: { url: '/api/archive', method: 'POST' } } },
    ],
    edges: [
      { id: 'e1', source: 'n1', target: 'n2' },
      { id: 'e2', source: 'n2', target: 'n3' },
      { id: 'e3', source: 'n3', target: 'n4', sourceHandle: 'body' },
      { id: 'e4', source: 'n4', target: 'n5' },
      { id: 'e5', source: 'n3', target: 'n6', sourceHandle: 'done' },
      { id: 'e6', source: 'n5', target: 'n3' },
      { id: 'e7', source: 'n6', target: 'n7' },
      { id: 'e8', source: 'n7', target: 'n8', sourceHandle: 'approved' },
      { id: 'e9', source: 'n7', target: 'n9', sourceHandle: 'approved' },
    ],
    nodeCount: 9, runCount: 0, lastRunAt: null, avgDurationMs: 0, successRate: 0,
    purpose: 'Run a full compliance audit: check all active regulatory frameworks, verify documents, score compliance, identify gaps, and generate a formal report for sign-off.',
    successCriteria: [
      { id: 'sc-9', description: 'All active frameworks must be checked', type: 'completeness', evaluation: 'deterministic', weight: 'critical' },
      { id: 'sc-10', description: 'Compliance scores must be backed by evidence documents', type: 'accuracy', evaluation: 'ai', weight: 'critical' },
      { id: 'sc-11', description: 'Report must include executive summary', type: 'format', evaluation: 'ai', weight: 'important' },
    ],
    constraints: ['ITAR data must not appear in report unless reviewer has clearance', 'All findings must cite the specific regulation clause'],
    supervisorMode: 'thorough',
    triggerType: 'manual',
    complexity: 'complex',
    agentId: 'agent-compliance', agentName: 'Compliance Monitor', agentIcon: 'shield-check', agentIconColor: 'violet',
    createdAt: daysAgo(7), updatedAt: daysAgo(1), createdBy: 'Admin',
  },
  // ── Simple Workflows (merged from old Automations) ──────────────────────
  {
    id: 'wf-5', name: 'Morning Shift Report', description: 'Automated daily shift handover report at 6 AM',
    status: 'active',
    nodes: [
      { id: 'n1', type: 'trigger', position: { x: 50, y: 200 }, data: { label: 'Daily 6 AM', config: { triggerType: 'schedule', cron: '0 6 * * *' } } },
      { id: 'n2', type: 'agent', position: { x: 300, y: 200 }, data: { label: 'Shift Handover', config: { agentId: 'agent-shift' } } },
    ],
    edges: [{ id: 'e1', source: 'n1', target: 'n2' }],
    nodeCount: 2, runCount: 240, lastRunAt: hoursAgo(6), avgDurationMs: 32400, successRate: 0.98,
    purpose: 'Generate and post the morning shift handover report to #operations channel.',
    successCriteria: [{ id: 'sc-12', description: 'Report must cover all alarms from night shift', type: 'completeness', evaluation: 'ai', weight: 'critical' }],
    constraints: [], supervisorMode: 'minimal',
    triggerType: 'schedule', cronExpression: '0 6 * * *',
    complexity: 'simple',
    agentId: 'agent-shift', agentName: 'Shift Handover', agentIcon: 'notebook', agentIconColor: 'amber',
    createdAt: daysAgo(120), updatedAt: daysAgo(1), createdBy: 'Jay Talreja',
  },
  {
    id: 'wf-6', name: 'Evening Shift Report', description: 'Automated daily shift handover report at 6 PM',
    status: 'active',
    nodes: [
      { id: 'n1', type: 'trigger', position: { x: 50, y: 200 }, data: { label: 'Daily 6 PM', config: { triggerType: 'schedule', cron: '0 18 * * *' } } },
      { id: 'n2', type: 'agent', position: { x: 300, y: 200 }, data: { label: 'Shift Handover', config: { agentId: 'agent-shift' } } },
    ],
    edges: [{ id: 'e1', source: 'n1', target: 'n2' }],
    nodeCount: 2, runCount: 238, lastRunAt: hoursAgo(18), avgDurationMs: 31500, successRate: 0.97,
    purpose: 'Generate and post the evening shift handover report.',
    successCriteria: [{ id: 'sc-13', description: 'Report covers all day shift activities', type: 'completeness', evaluation: 'ai', weight: 'critical' }],
    constraints: [], supervisorMode: 'minimal',
    triggerType: 'schedule', cronExpression: '0 18 * * *',
    complexity: 'simple',
    agentId: 'agent-shift', agentName: 'Shift Handover', agentIcon: 'notebook', agentIconColor: 'amber',
    createdAt: daysAgo(120), updatedAt: daysAgo(1), createdBy: 'Jay Talreja',
  },
  {
    id: 'wf-7', name: 'Weekly Reliability Report', description: 'Generate reliability analysis with MTBF, MTTR, and failure trends every Monday',
    status: 'active',
    nodes: [
      { id: 'n1', type: 'trigger', position: { x: 50, y: 200 }, data: { label: 'Monday 9 AM', config: { triggerType: 'schedule', cron: '0 9 * * 1' } } },
      { id: 'n2', type: 'agent', position: { x: 300, y: 200 }, data: { label: 'Reliability', config: { agentId: 'agent-reliability' } } },
    ],
    edges: [{ id: 'e1', source: 'n1', target: 'n2' }],
    nodeCount: 2, runCount: 52, lastRunAt: daysAgo(3), avgDurationMs: 115000, successRate: 0.95,
    purpose: 'Analyze equipment reliability metrics and produce a weekly report for the maintenance team.',
    successCriteria: [{ id: 'sc-14', description: 'Report includes MTBF and MTTR for all critical equipment', type: 'completeness', evaluation: 'ai', weight: 'critical' }],
    constraints: [], supervisorMode: 'minimal',
    triggerType: 'schedule', cronExpression: '0 9 * * 1',
    complexity: 'simple',
    agentId: 'agent-reliability', agentName: 'Reliability', agentIcon: 'bar-chart', agentIconColor: 'emerald',
    createdAt: daysAgo(365), updatedAt: daysAgo(3), createdBy: 'Sarah Johnson',
  },
  {
    id: 'wf-8', name: 'High Vibration Alert', description: 'Trigger maintenance analysis when equipment vibration exceeds threshold',
    status: 'active',
    nodes: [
      { id: 'n1', type: 'trigger', position: { x: 50, y: 200 }, data: { label: 'Threshold Exceeded', config: { triggerType: 'event', eventType: 'threshold_exceeded' } } },
      { id: 'n2', type: 'agent', position: { x: 300, y: 200 }, data: { label: 'Maintenance Intelligence', config: { agentId: 'agent-maintenance' } } },
    ],
    edges: [{ id: 'e1', source: 'n1', target: 'n2' }],
    nodeCount: 2, runCount: 47, lastRunAt: hoursAgo(2), avgDurationMs: 48200, successRate: 0.92,
    purpose: 'Analyze vibration data and recommend corrective action when thresholds are exceeded.',
    successCriteria: [{ id: 'sc-15', description: 'Analysis must include trend data and ISO comparison', type: 'accuracy', evaluation: 'ai', weight: 'critical' }],
    constraints: ['Never recommend shutdown without supervisor approval'], supervisorMode: 'standard',
    triggerType: 'event', eventType: 'threshold_exceeded',
    complexity: 'simple',
    agentId: 'agent-maintenance', agentName: 'Maintenance Intelligence', agentIcon: 'wrench', agentIconColor: 'cyan',
    createdAt: daysAgo(90), updatedAt: daysAgo(2), createdBy: 'Mike Chen',
  },
  {
    id: 'wf-9', name: 'Daily Compliance Check', description: 'Run compliance checks against ITAR and CMMC frameworks every morning',
    status: 'paused',
    nodes: [
      { id: 'n1', type: 'trigger', position: { x: 50, y: 200 }, data: { label: 'Daily 8 AM', config: { triggerType: 'schedule', cron: '0 8 * * *' } } },
      { id: 'n2', type: 'agent', position: { x: 300, y: 200 }, data: { label: 'Compliance Monitor', config: { agentId: 'agent-compliance' } } },
    ],
    edges: [{ id: 'e1', source: 'n1', target: 'n2' }],
    nodeCount: 2, runCount: 180, lastRunAt: hoursAgo(12), avgDurationMs: 58000, successRate: 0.85,
    purpose: 'Check compliance status against all active regulatory frameworks.',
    successCriteria: [{ id: 'sc-16', description: 'All active frameworks checked', type: 'completeness', evaluation: 'deterministic', weight: 'critical' }],
    constraints: ['ITAR findings must be flagged for immediate review'], supervisorMode: 'standard',
    triggerType: 'schedule', cronExpression: '0 8 * * *',
    complexity: 'simple',
    agentId: 'agent-compliance', agentName: 'Compliance Monitor', agentIcon: 'shield-check', agentIconColor: 'violet',
    createdAt: daysAgo(200), updatedAt: hoursAgo(12), createdBy: 'Admin',
  },
  {
    id: 'wf-10', name: 'Work Order Overdue Alert', description: 'Alert work order planner when work orders exceed due date by 24+ hours',
    status: 'active',
    nodes: [
      { id: 'n1', type: 'trigger', position: { x: 50, y: 200 }, data: { label: 'WO Overdue', config: { triggerType: 'event', eventType: 'work_order_overdue' } } },
      { id: 'n2', type: 'agent', position: { x: 300, y: 200 }, data: { label: 'Work Order Planner', config: { agentId: 'agent-work-order' } } },
    ],
    edges: [{ id: 'e1', source: 'n1', target: 'n2' }],
    nodeCount: 2, runCount: 23, lastRunAt: daysAgo(1), avgDurationMs: 35000, successRate: 0.88,
    purpose: 'Analyze overdue work orders and recommend rescheduling priorities.',
    successCriteria: [{ id: 'sc-17', description: 'All overdue WOs identified with risk assessment', type: 'completeness', evaluation: 'ai', weight: 'critical' }],
    constraints: [], supervisorMode: 'minimal',
    triggerType: 'event', eventType: 'work_order_overdue',
    complexity: 'simple',
    agentId: 'agent-work-order', agentName: 'Work Order Planner', agentIcon: 'clipboard-check', agentIconColor: 'amber',
    createdAt: daysAgo(60), updatedAt: daysAgo(1), createdBy: 'Sarah Johnson',
  },
  {
    id: 'wf-11', name: 'Certification Expiring Alert', description: 'Notify compliance team 90 days before any certification expires',
    status: 'active',
    nodes: [
      { id: 'n1', type: 'trigger', position: { x: 50, y: 200 }, data: { label: 'Cert Expiring', config: { triggerType: 'event', eventType: 'certification_expiring' } } },
      { id: 'n2', type: 'agent', position: { x: 300, y: 200 }, data: { label: 'Compliance Monitor', config: { agentId: 'agent-compliance' } } },
    ],
    edges: [{ id: 'e1', source: 'n1', target: 'n2' }],
    nodeCount: 2, runCount: 12, lastRunAt: daysAgo(5), avgDurationMs: 22000, successRate: 1.0,
    purpose: 'Track certification expiry dates and alert the compliance team with renewal action items.',
    successCriteria: [{ id: 'sc-18', description: 'Alert must include certification details and renewal steps', type: 'completeness', evaluation: 'ai', weight: 'important' }],
    constraints: [], supervisorMode: 'minimal',
    triggerType: 'event', eventType: 'certification_expiring',
    complexity: 'simple',
    agentId: 'agent-compliance', agentName: 'Compliance Monitor', agentIcon: 'shield-check', agentIconColor: 'violet',
    createdAt: daysAgo(180), updatedAt: daysAgo(5), createdBy: 'Admin',
  },
];

// ── Mock Playbooks ──────────────────────────────────────────────────────────

export const MOCK_PLAYBOOKS: Playbook[] = [
  {
    id: 'pb-1', name: 'Pump Vibration Troubleshooting', description: 'Guided multi-step diagnosis for pump vibration issues. Checks asset data, telemetry trends, maintenance history, and ISO standards to produce a diagnosis with recommended actions.',
    category: 'maintenance', agentId: 'agent-troubleshooting', agentName: 'Troubleshooting', agentIcon: 'search', agentIconColor: 'amber',
    steps: [
      { step: 1, name: 'Asset Lookup', description: 'Retrieve equipment details, criticality, and location from the asset registry', toolUsed: 'iq.asset.search', onFailure: 'stop' },
      { step: 2, name: 'Vibration Data Retrieval', description: 'Pull 30-day vibration trend data from telemetry sensors', toolUsed: 'iq.telemetry.getHistory', onFailure: 'stop' },
      { step: 3, name: 'Maintenance History Check', description: 'Review recent maintenance activities, past work orders, and bearing replacements', toolUsed: 'iq.asset.getHistory', onFailure: 'continue' },
      { step: 4, name: 'ISO 10816 Comparison', description: 'Compare current readings against ISO vibration severity thresholds', toolUsed: 'builtin.search_knowledge', onFailure: 'continue' },
      { step: 5, name: 'Manufacturer Specs Check', description: 'Search for bearing specifications, torque values, and service intervals', toolUsed: 'builtin.search_knowledge', onFailure: 'continue' },
      { step: 6, name: 'Diagnosis & Recommendations', description: 'Synthesize all findings into a diagnosis with recommended actions and urgency', onFailure: 'stop' },
    ],
    estimatedDuration: '45-60 seconds', successRate: 0.92, tags: ['vibration', 'bearing', 'pump', 'iso-10816'],
  },
  {
    id: 'pb-2', name: 'PM Overdue Analysis', description: 'Analyze overdue preventive maintenance work orders, assess risk based on equipment criticality, and recommend rescheduling priorities.',
    category: 'maintenance', agentId: 'agent-maintenance', agentName: 'Maintenance Intelligence', agentIcon: 'wrench', agentIconColor: 'cyan',
    steps: [
      { step: 1, name: 'List Overdue PMs', description: 'Query CMMS for all PM work orders past their due date', toolUsed: 'iq.cmms.getWorkOrders', onFailure: 'stop' },
      { step: 2, name: 'Equipment Health Assessment', description: 'For each overdue PM, check the equipment health indicators and recent telemetry', toolUsed: 'iq.telemetry.getLatest', onFailure: 'continue' },
      { step: 3, name: 'Risk Ranking', description: 'Rank overdue PMs by risk based on equipment criticality, health indicators, and days overdue', onFailure: 'stop' },
      { step: 4, name: 'Schedule Recommendation', description: 'Generate a recommended PM schedule with priorities and resource requirements', onFailure: 'stop' },
    ],
    estimatedDuration: '30-45 seconds', successRate: 0.95, tags: ['pm', 'overdue', 'scheduling', 'risk'],
  },
  {
    id: 'pb-3', name: 'Compliance Audit', description: 'Comprehensive compliance audit workflow. Checks all active regulatory frameworks, gathers evidence, scores compliance, identifies gaps, and generates a formal report.',
    category: 'compliance', agentId: 'agent-compliance', agentName: 'Compliance Monitor', agentIcon: 'shield-check', agentIconColor: 'violet',
    steps: [
      { step: 1, name: 'List Active Frameworks', description: 'Identify all active compliance frameworks (ITAR, CMMC, HIPAA, GDPR)', toolUsed: 'iq.compliance.getFrameworks', onFailure: 'stop' },
      { step: 2, name: 'Check Compliance Items', description: 'For each framework, verify compliance status of each control item', toolUsed: 'iq.compliance.checkStatus', onFailure: 'continue' },
      { step: 3, name: 'Gather Evidence', description: 'Collect supporting documents, audit logs, and certification records', toolUsed: 'builtin.search_knowledge', onFailure: 'continue' },
      { step: 4, name: 'Score Compliance', description: 'Calculate compliance scores per framework and overall', onFailure: 'stop' },
      { step: 5, name: 'Identify Gaps', description: 'Flag non-compliant items with remediation recommendations', onFailure: 'stop' },
      { step: 6, name: 'Generate Report', description: 'Compile formal compliance report with executive summary, scores, and action items', onFailure: 'stop' },
    ],
    estimatedDuration: '2-5 minutes', successRate: 0.88, tags: ['itar', 'cmmc', 'hipaa', 'gdpr', 'audit'],
  },
  {
    id: 'pb-4', name: 'LOTO Procedure Lookup', description: 'Safety-critical procedure lookup for Lock-Out/Tag-Out. Verifies equipment isolation points, PPE requirements, and operator qualifications.',
    category: 'safety', agentId: 'agent-safety', agentName: 'Safety & LOTO', agentIcon: 'alert-triangle', agentIconColor: 'red',
    steps: [
      { step: 1, name: 'Asset Identification', description: 'Look up equipment details and connected systems', toolUsed: 'iq.asset.get', onFailure: 'stop' },
      { step: 2, name: 'LOTO Procedure Search', description: 'Find the correct LOTO procedure for this equipment', toolUsed: 'iq.document.search', onFailure: 'stop' },
      { step: 3, name: 'Safety Tag Verification', description: 'Check for any active safety tags or permits on the equipment', toolUsed: 'iq.safety.getActiveTags', onFailure: 'stop' },
      { step: 4, name: 'Permit Check', description: 'Verify the operator has required qualifications and training currency', toolUsed: 'iq.asset.getPersonnel', onFailure: 'stop' },
      { step: 5, name: 'Procedure Display', description: 'Present the step-by-step LOTO procedure with all isolation points and PPE requirements', onFailure: 'stop' },
    ],
    estimatedDuration: '15-30 seconds', successRate: 0.97, tags: ['loto', 'safety', 'lockout', 'tagout', 'permit'],
  },
  {
    id: 'pb-5', name: 'Shift Handover Report', description: 'Automated shift handover report generation. Collects all shift data and compiles a comprehensive handover document.',
    category: 'operations', agentId: 'agent-shift', agentName: 'Shift Handover', agentIcon: 'notebook', agentIconColor: 'amber',
    steps: [
      { step: 1, name: 'Collect Alarms', description: 'Retrieve all alarms triggered during the shift period', toolUsed: 'iq.telemetry.getAlarms', onFailure: 'continue' },
      { step: 2, name: 'Collect Work Orders', description: 'Get all work orders completed, in progress, and created during shift', toolUsed: 'iq.cmms.getWorkOrders', onFailure: 'continue' },
      { step: 3, name: 'Maintenance Activities', description: 'List all maintenance activities performed during the shift', toolUsed: 'iq.cmms.getActivities', onFailure: 'continue' },
      { step: 4, name: 'Open Issues', description: 'Identify any unresolved issues that need attention from the next shift', onFailure: 'continue' },
      { step: 5, name: 'Safety Incidents', description: 'Check for any safety incidents, near-misses, or observations', toolUsed: 'iq.safety.getIncidents', onFailure: 'continue' },
      { step: 6, name: 'Compile Handover', description: 'Generate the formatted shift handover document with all collected data', onFailure: 'stop' },
    ],
    estimatedDuration: '30-45 seconds', successRate: 0.98, tags: ['shift', 'handover', 'report', 'operations'],
  },
  {
    id: 'pb-6', name: 'Root Cause Analysis', description: 'Systematic incident investigation. Builds timeline, identifies affected systems, correlates with historical data, and produces an RCA report.',
    category: 'operations', agentId: 'agent-incident', agentName: 'Incident Analysis', agentIcon: 'flame', agentIconColor: 'red',
    steps: [
      { step: 1, name: 'Incident Timeline', description: 'Build a chronological timeline of events leading up to and during the incident', toolUsed: 'iq.telemetry.getHistory', onFailure: 'stop' },
      { step: 2, name: 'Affected Equipment', description: 'Identify all equipment involved and their current state', toolUsed: 'iq.asset.search', onFailure: 'continue' },
      { step: 3, name: 'Upstream/Downstream', description: 'Analyze upstream and downstream systems for cascade effects', toolUsed: 'iq.asset.getConnected', onFailure: 'continue' },
      { step: 4, name: 'Historical Incidents', description: 'Search for similar past incidents on the same or related equipment', toolUsed: 'iq.asset.getHistory', onFailure: 'continue' },
      { step: 5, name: 'Correlation Analysis', description: 'Correlate current incident with historical patterns and known failure modes', toolUsed: 'builtin.search_knowledge', onFailure: 'continue' },
      { step: 6, name: 'RCA Report', description: 'Generate root cause analysis report with findings, contributing factors, and recommendations', onFailure: 'stop' },
    ],
    estimatedDuration: '1-2 minutes', successRate: 0.85, tags: ['rca', 'incident', 'root-cause', 'investigation'],
  },
  {
    id: 'pb-7', name: 'Work Order Generation', description: 'Intelligent work order creation. Gathers asset data, checks parts availability, finds scheduling slots, and generates a complete work order.',
    category: 'maintenance', agentId: 'agent-work-order', agentName: 'Work Order Planner', agentIcon: 'clipboard-check', agentIconColor: 'amber',
    steps: [
      { step: 1, name: 'Asset Details', description: 'Retrieve comprehensive asset information including specifications and service history', toolUsed: 'iq.asset.get', onFailure: 'stop' },
      { step: 2, name: 'Maintenance Requirements', description: 'Determine required maintenance tasks based on condition and history', toolUsed: 'builtin.search_knowledge', onFailure: 'continue' },
      { step: 3, name: 'Parts Availability', description: 'Check inventory for required parts and order if needed', toolUsed: 'ext.sap.checkStock', onFailure: 'continue' },
      { step: 4, name: 'Schedule Availability', description: 'Find optimal maintenance window considering production schedule', toolUsed: 'iq.cmms.getSchedule', onFailure: 'continue' },
      { step: 5, name: 'Resource Allocation', description: 'Identify available technicians with required skills and certifications', toolUsed: 'iq.asset.getPersonnel', onFailure: 'continue' },
      { step: 6, name: 'Generate Work Order', description: 'Create the work order with all details: tasks, parts, schedule, resources, and safety requirements', toolUsed: 'iq.cmms.createWorkOrder', onFailure: 'stop' },
    ],
    estimatedDuration: '30-60 seconds', successRate: 0.90, tags: ['work-order', 'planning', 'parts', 'scheduling'],
  },
];

// ── Mock Execution History ──────────────────────────────────────────────────

export const MOCK_EXECUTIONS: ExecutionEntry[] = [
  { id: 'ex-1', type: 'scheduled', sourceId: 'auto-1', sourceName: 'Morning Shift Report', agentId: 'agent-shift', agentName: 'Shift Handover', agentIcon: 'notebook', agentIconColor: 'amber', status: 'completed', trigger: 'Cron: 0 6 * * *', startedAt: hoursAgo(6), completedAt: hoursAgo(5.99), durationMs: 32400, stepsCompleted: 6, stepsFailed: 0, steps: [
    { step: 1, name: 'Collect Alarms', toolUsed: 'iq.telemetry.getAlarms', status: 'completed', durationMs: 4200 },
    { step: 2, name: 'Collect Work Orders', toolUsed: 'iq.cmms.getWorkOrders', status: 'completed', durationMs: 5800 },
    { step: 3, name: 'Maintenance Activities', toolUsed: 'iq.cmms.getActivities', status: 'completed', durationMs: 3900 },
    { step: 4, name: 'Open Issues', status: 'completed', durationMs: 6200 },
    { step: 5, name: 'Safety Incidents', toolUsed: 'iq.safety.getIncidents', status: 'completed', durationMs: 3100 },
    { step: 6, name: 'Compile Handover', status: 'completed', durationMs: 9200 },
  ] },
  { id: 'ex-2', type: 'event', sourceId: 'auto-6', sourceName: 'High Vibration Alert', agentId: 'agent-maintenance', agentName: 'Maintenance Intelligence', agentIcon: 'wrench', agentIconColor: 'cyan', status: 'completed', trigger: 'threshold_exceeded: P-401 vibration 4.8 mm/s', startedAt: hoursAgo(2), completedAt: hoursAgo(1.98), durationMs: 48200, stepsCompleted: 5, stepsFailed: 0, steps: [
    { step: 1, name: 'Asset Lookup', toolUsed: 'iq.asset.search', status: 'completed', durationMs: 3200 },
    { step: 2, name: 'Vibration Data Retrieval', toolUsed: 'iq.telemetry.getHistory', status: 'completed', durationMs: 8400 },
    { step: 3, name: 'Maintenance History Check', toolUsed: 'iq.asset.getHistory', status: 'completed', durationMs: 6100 },
    { step: 4, name: 'ISO 10816 Comparison', toolUsed: 'builtin.search_knowledge', status: 'completed', durationMs: 12500 },
    { step: 5, name: 'Diagnosis & Recommendations', status: 'completed', durationMs: 18000 },
  ] },
  { id: 'ex-3', type: 'workflow', sourceId: 'wf-2', sourceName: 'Shift Handover Pipeline', status: 'completed', trigger: 'Cron: 0 18 * * *', startedAt: hoursAgo(18), completedAt: hoursAgo(17.99), durationMs: 31000, stepsCompleted: 7, stepsFailed: 0, steps: [
    { step: 1, name: 'Schedule Trigger', status: 'completed', durationMs: 200 },
    { step: 2, name: 'Get Alarms', toolUsed: 'iq.telemetry.getAlarms', status: 'completed', durationMs: 4100 },
    { step: 3, name: 'Get Work Orders', toolUsed: 'iq.cmms.getWorkOrders', status: 'completed', durationMs: 5200 },
    { step: 4, name: 'Get Safety Log', toolUsed: 'iq.safety.getIncidents', status: 'completed', durationMs: 3600 },
    { step: 5, name: 'Compile Report', status: 'completed', durationMs: 11400 },
    { step: 6, name: 'Post to Slack', status: 'completed', durationMs: 2800 },
    { step: 7, name: 'Email Report', status: 'completed', durationMs: 3700 },
  ] },
  { id: 'ex-4', type: 'scheduled', sourceId: 'auto-4', sourceName: 'Daily Compliance Check', agentId: 'agent-compliance', agentName: 'Compliance Monitor', agentIcon: 'shield-check', agentIconColor: 'violet', status: 'failed', trigger: 'Cron: 0 8 * * *', startedAt: hoursAgo(12), completedAt: hoursAgo(11.98), durationMs: 65400, stepsCompleted: 3, stepsFailed: 1, error: 'Step 4 failed: CMMC framework check returned timeout after 60s', steps: [
    { step: 1, name: 'List Active Frameworks', toolUsed: 'iq.compliance.getFrameworks', status: 'completed', durationMs: 2800 },
    { step: 2, name: 'Check ITAR Status', toolUsed: 'iq.compliance.checkStatus', status: 'completed', durationMs: 12400 },
    { step: 3, name: 'Check HIPAA Status', toolUsed: 'iq.compliance.checkStatus', status: 'completed', durationMs: 11200 },
    { step: 4, name: 'Check CMMC Status', toolUsed: 'iq.compliance.checkStatus', status: 'failed', durationMs: 60000, error: 'CMMC framework check returned timeout after 60s' },
  ] },
  { id: 'ex-5', type: 'workflow', sourceId: 'wf-1', sourceName: 'Equipment Troubleshooting', status: 'completed', trigger: 'Event: threshold_exceeded', startedAt: hoursAgo(26), completedAt: hoursAgo(25.98), durationMs: 42000, stepsCompleted: 8, stepsFailed: 0 },
  { id: 'ex-6', type: 'manual', sourceId: 'auto-3', sourceName: 'Weekly Reliability Report', agentId: 'agent-reliability', agentName: 'Reliability', agentIcon: 'bar-chart', agentIconColor: 'emerald', status: 'completed', trigger: 'Manual by Jay Talreja', startedAt: daysAgo(1), completedAt: new Date(Date.now() - 86400000 + 120000).toISOString(), durationMs: 120000, stepsCompleted: 4, stepsFailed: 0 },
  { id: 'ex-7', type: 'event', sourceId: 'auto-7', sourceName: 'Work Order Overdue', agentId: 'agent-work-order', agentName: 'Work Order Planner', agentIcon: 'clipboard-check', agentIconColor: 'amber', status: 'completed', trigger: 'work_order_overdue: WO-3015', startedAt: daysAgo(1), completedAt: new Date(Date.now() - 86400000 + 35000).toISOString(), durationMs: 35000, stepsCompleted: 3, stepsFailed: 0 },
  { id: 'ex-8', type: 'scheduled', sourceId: 'auto-1', sourceName: 'Morning Shift Report', agentId: 'agent-shift', agentName: 'Shift Handover', agentIcon: 'notebook', agentIconColor: 'amber', status: 'completed', trigger: 'Cron: 0 6 * * *', startedAt: daysAgo(1.25), completedAt: new Date(Date.now() - 108000000 + 33000).toISOString(), durationMs: 33000, stepsCompleted: 6, stepsFailed: 0 },
  { id: 'ex-9', type: 'workflow', sourceId: 'wf-3', sourceName: 'Document Onboarding', status: 'completed', trigger: 'Event: document_uploaded', startedAt: daysAgo(2), completedAt: new Date(Date.now() - 172800000 + 68000).toISOString(), durationMs: 68000, stepsCompleted: 6, stepsFailed: 0 },
  { id: 'ex-10', type: 'scheduled', sourceId: 'auto-2', sourceName: 'Evening Shift Report', agentId: 'agent-shift', agentName: 'Shift Handover', agentIcon: 'notebook', agentIconColor: 'amber', status: 'completed', trigger: 'Cron: 0 18 * * *', startedAt: daysAgo(2.25), completedAt: new Date(Date.now() - 194400000 + 31500).toISOString(), durationMs: 31500, stepsCompleted: 6, stepsFailed: 0 },
  { id: 'ex-11', type: 'event', sourceId: 'auto-6', sourceName: 'High Vibration Alert', agentId: 'agent-maintenance', agentName: 'Maintenance Intelligence', agentIcon: 'wrench', agentIconColor: 'cyan', status: 'failed', trigger: 'threshold_exceeded: FW-201 vibration 5.1 mm/s', startedAt: daysAgo(3), completedAt: new Date(Date.now() - 259200000 + 15000).toISOString(), durationMs: 15000, stepsCompleted: 2, stepsFailed: 1, error: 'Agent timeout: Maintenance Intelligence did not respond within 30s', steps: [
    { step: 1, name: 'Asset Lookup', toolUsed: 'iq.asset.search', status: 'completed', durationMs: 3400 },
    { step: 2, name: 'Vibration Data Retrieval', toolUsed: 'iq.telemetry.getHistory', status: 'completed', durationMs: 7600 },
    { step: 3, name: 'Maintenance History Check', toolUsed: 'iq.asset.getHistory', status: 'failed', durationMs: 30000, error: 'Agent timeout: Maintenance Intelligence did not respond within 30s' },
  ] },
  { id: 'ex-12', type: 'scheduled', sourceId: 'auto-4', sourceName: 'Daily Compliance Check', agentId: 'agent-compliance', agentName: 'Compliance Monitor', agentIcon: 'shield-check', agentIconColor: 'violet', status: 'completed', trigger: 'Cron: 0 8 * * *', startedAt: daysAgo(3.5), completedAt: new Date(Date.now() - 302400000 + 58000).toISOString(), durationMs: 58000, stepsCompleted: 4, stepsFailed: 0 },
  { id: 'ex-13', type: 'workflow', sourceId: 'wf-1', sourceName: 'Equipment Troubleshooting', status: 'completed', trigger: 'Event: threshold_exceeded', startedAt: daysAgo(5), completedAt: new Date(Date.now() - 432000000 + 44000).toISOString(), durationMs: 44000, stepsCompleted: 8, stepsFailed: 0 },
  { id: 'ex-14', type: 'event', sourceId: 'auto-8', sourceName: 'Certification Expiring', agentId: 'agent-compliance', agentName: 'Compliance Monitor', agentIcon: 'shield-check', agentIconColor: 'violet', status: 'completed', trigger: 'certification_expiring: CMMC L2 (90 days)', startedAt: daysAgo(5), completedAt: new Date(Date.now() - 432000000 + 22000).toISOString(), durationMs: 22000, stepsCompleted: 3, stepsFailed: 0 },
  { id: 'ex-15', type: 'scheduled', sourceId: 'auto-3', sourceName: 'Weekly Reliability Report', agentId: 'agent-reliability', agentName: 'Reliability', agentIcon: 'bar-chart', agentIconColor: 'emerald', status: 'completed', trigger: 'Cron: 0 9 * * 1', startedAt: daysAgo(7), completedAt: new Date(Date.now() - 604800000 + 115000).toISOString(), durationMs: 115000, stepsCompleted: 4, stepsFailed: 0 },
  { id: 'ex-16', type: 'workflow', sourceId: 'wf-2', sourceName: 'Shift Handover Pipeline', status: 'cancelled', trigger: 'Cron: 0 6 * * *', startedAt: daysAgo(8), completedAt: daysAgo(8), durationMs: 5000, stepsCompleted: 1, stepsFailed: 0, error: 'Cancelled by user during execution' },
  { id: 'ex-17', type: 'scheduled', sourceId: 'auto-1', sourceName: 'Morning Shift Report', agentId: 'agent-shift', agentName: 'Shift Handover', agentIcon: 'notebook', agentIconColor: 'amber', status: 'completed', trigger: 'Cron: 0 6 * * *', startedAt: daysAgo(10), completedAt: new Date(Date.now() - 864000000 + 34000).toISOString(), durationMs: 34000, stepsCompleted: 6, stepsFailed: 0 },
  { id: 'ex-18', type: 'playbook', sourceId: 'pb-1', sourceName: 'Pump Vibration Troubleshooting', agentId: 'agent-troubleshooting', agentName: 'Troubleshooting', agentIcon: 'search', agentIconColor: 'amber', status: 'completed', trigger: 'Manual by Mike Chen', startedAt: daysAgo(12), completedAt: new Date(Date.now() - 1036800000 + 52000).toISOString(), durationMs: 52000, stepsCompleted: 6, stepsFailed: 0 },
  { id: 'ex-19', type: 'playbook', sourceId: 'pb-5', sourceName: 'Shift Handover Report', agentId: 'agent-shift', agentName: 'Shift Handover', agentIcon: 'notebook', agentIconColor: 'amber', status: 'completed', trigger: 'Manual by Jay Talreja', startedAt: daysAgo(15), completedAt: new Date(Date.now() - 1296000000 + 38000).toISOString(), durationMs: 38000, stepsCompleted: 6, stepsFailed: 0 },
  { id: 'ex-20', type: 'manual', sourceId: 'auto-5', sourceName: 'Quarterly ITAR Audit', agentId: 'agent-compliance', agentName: 'Compliance Monitor', agentIcon: 'shield-check', agentIconColor: 'violet', status: 'completed', trigger: 'Manual by Admin', startedAt: daysAgo(45), completedAt: new Date(Date.now() - 3888000000 + 380000).toISOString(), durationMs: 380000, stepsCompleted: 6, stepsFailed: 0 },
];
